import React, {
    useEffect, useRef, useState
} from 'react';
import {
    TooltipProvider, Tooltip, TooltipTrigger, TooltipContent
} from 'shared/components/ui/tooltip';
import {
    Label
} from 'shared/components/ui/label';
import {
    Button
} from 'shared/components/ui/button';
import {
    Info
} from 'lucide-react';
import {
    Textarea
} from 'shared/components/ui/textarea';

export default function StyledTextAreaCn({
    value,
    onChange,
    labelText,
    rows = 2,
    tooltipText,
    placeholder,
    disabled = false,
    required = false,
    readOnly = false,
    actions = null,
}) {
    const textareaRef = useRef(null);
    const [isFocused, setIsFocused] = useState(false);

    const adjustTextareaHeight = () => {
        const textarea = textareaRef.current;
        if (!textarea) return;
        textarea.style.height = 'auto';
        textarea.style.height = `${textarea.scrollHeight}px`;
    };

    useEffect(() => {
        adjustTextareaHeight();
    }, [value]);

    const handleChange = (e) => {
        adjustTextareaHeight();
        onChange?.(e);
    };

    return (
        <div className="flex flex-col gap-[2px] h-fit w-full">
            <div className="flex flex-row items-center gap-2">
                <TooltipProvider className="flex items-center justify-between">
                    {labelText && (
                        <Label htmlFor="email" className="select-none text-xs">
                            {labelText} {required && <span className="text-red-500">*</span>}
                        </Label>
                    )}

                    {tooltipText && (
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <Button variant="ghost" size="icon">
                                    <Info className="h-4 w-4" />
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent className="w-[300px]">
                                <p>{tooltipText}</p>
                            </TooltipContent>
                        </Tooltip>
                    )}
                </TooltipProvider>
            </div>
            <div className={`border rounded-md h-fit ${isFocused ? 'ring-1 ring-gray-400' : ''}`}>
                <Textarea
                    ref={textareaRef}
                    placeholder={placeholder ? placeholder : ''}
                    value={value}
                    onChange={handleChange}
                    onFocus={() => setIsFocused(true)}
                    onBlur={() => setIsFocused(false)}
                    className="dynamicTextArea w-full p-2 border border-gray-300 rounded-md text-xs border-none focus-visible:ring-0 shadow-none"
                    disabled={disabled}
                    readOnly={readOnly}
                    rows={rows}
                    style={{
                        resize: 'none',
                        overflow: 'hidden',
                        minHeight: `${rows * 1.5}rem`
                    }}
                />
                {
                    actions && actions.some(action => action.visible) &&
                    (<div className="flex w-full justify-end select-none cursor-text">
                        <div className="flex gap-2 px-2 pb-2 cursor-default">
                            {actions.filter(action => action.visible).map((action, index) => {
                                const button = (
                                    <Button
                                        type="button"
                                        variant="outline"
                                        className={`px-2 shadow-md opacity-90 hover:opacity-100 cursor-pointer ${action?.disabled ? 'pointer-events-none' : ''}`}
                                        onClick={action.onClick}
                                        disabled={action?.disabled}
                                    >
                                        {action?.icon}
                                    </Button>
                                );

                                if (!action.tooltip) {
                                    return (
                                        <span key={index} className="inline-flex">
                                            {button}
                                        </span>
                                    );
                                }

                                return (
                                    <TooltipProvider delayDuration={150} key={index}>
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <span className="inline-flex">
                                                    {button}
                                                </span>
                                            </TooltipTrigger>
                                            <TooltipContent className="max-w-xs text-xs leading-relaxed">
                                                {action.tooltip}
                                            </TooltipContent>
                                        </Tooltip>
                                    </TooltipProvider>
                                );
                            })}
                        </div>
                    </div>)
                }

            </div>
        </div>
    );
}
